import { observer } from 'mobx-react';
import classNames from 'classnames';

import styles from './EhrInsuranceInfo.module.scss';

import { EhrPlanData } from 'chaika-frontend-types/frontend-cms';

import { TextLink } from '@components/TextLink';
import { LinkTarget, TextLinkModifiers } from '@components/TextLink/TextLink';
import { EhrPageNames } from '@patient/store/constants';
import { translate } from '@chaika/i18n/helper';
import { Icon, IconType } from '@components/Icon';
import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { Text, TextModifiers } from '@components/Text/Text';
import { Ehr } from '@patient/store/ehr/Ehr';
import { useLegalEntityMapQuery } from '@organization/hooks/legalEntity/useLegalEntityMap';
import { ServicePlanRow } from '@common/CreationMenuFields/EhrInsuranceInfo/ServicePlanRow';

interface Props {
    value: ValueData;
    dataTest: string;
}

interface ValueData {
    ehr: Ehr;
    servicePlans: EhrPlanData[];
}

export const EhrInsuranceInfo = observer(({ value, dataTest }: Props) => {
    const { servicePlans, ehr } = value;
    const ehrUrl = ehr ? `/patient/${ehr.recordId}` : '';

    const payerIds = servicePlans.map(plan => plan.payerId).filter((id): id is string => !!id);
    const { map: legalEntityMap, isFetched } = useLegalEntityMapQuery(payerIds);

    return (
        <div
            className={styles.root}
            data-test={dataTest}
            data-testid={dataTest}
        >
            <FieldTitle
                theme={FieldTitleTheme.BigBold}
                title={
                    <div className={styles.textWithIcon}>
                        <TextLink
                            modifiers={[TextLinkModifiers.Highlighted]}
                            target={LinkTarget.BLANK}
                            link={`${ehrUrl}?currentPage=${EhrPageNames.Programs}`}
                            label={translate('insurancePrograms')}
                        />
                        <div className={classNames([styles.icon, styles.okIcon])}>
                            <Icon type={IconType.OK_SHIELD} />
                        </div>
                    </div>
                }
            />
            <div className={styles.rows}>
                {servicePlans.map(it => (
                    <ServicePlanRow
                        servicePlan={it}
                        legalEntityMap={legalEntityMap}
                        isFetched={isFetched}
                        key={it.id}
                    />
                ))}
            </div>
            {ehr.hasBankCard && (
                <>
                    <FieldTitle
                        theme={FieldTitleTheme.BigBold}
                        title={
                            <div className={styles.textWithIcon}>
                                <TextLink
                                    modifiers={[TextLinkModifiers.Highlighted]}
                                    target={LinkTarget.BLANK}
                                    link={`${ehrUrl}?currentPage=${EhrPageNames.RenderedService}`}
                                    label={translate('servicesAndPayment')}
                                />
                                <div className={classNames([styles.icon, styles.creditCardIcon])}>
                                    <Icon type={IconType.CREDIT_CARD} />
                                </div>
                            </div>
                        }
                    />
                    <Text
                        modifiers={[TextModifiers.SmallText]}
                        text={translate('hasLinkedCard')}
                    />
                </>
            )}
        </div>
    );
});
